/**
 * JourneyPage — public client route: /journey?t=<token>
 *
 * No Clerk, no login. Reads the token, loads the journey, and renders the misty
 * background with the focused-entry shell on top. Friendly states for loading,
 * an invalid/expired link, and errors.
 *
 * The page body is JourneyView({ token }), exported so the team's preview (JourneyPreviewOverlay)
 * renders the very same view with a preview pass in place of a client's token
 * (docs/CLIENT_HUB_PREVIEW.md) — one view, so the preview cannot drift from the real thing.
 */

import { useState, useRef, useEffect } from 'react'
import { useSearchParams } from 'react-router-dom'
import { useAuth } from '@clerk/clerk-react'
import MistyBackground from './MistyBackground.jsx'
import ConceptDrawingHost from '../components/ConceptDrawingModal.jsx'
import JourneyShell from './JourneyShell.jsx'
import JourneyBilling from './JourneyBilling.jsx'
import BillingHoldOverlay from './BillingHoldOverlay.jsx'
import JourneyPinGate from './JourneyPinGate.jsx'
import TestPanel from './TestPanel.jsx'
import HubTour from './HubTour.jsx'
import { useJourney } from './useJourney.js'
import { isPreviewToken } from '../services/journey.js'

export default function JourneyPage() {
  const [params] = useSearchParams()
  const token = params.get('t')
  // An older Hub link ending in &open=inspiration is forwarded to the board's own tab.
  useEffect(() => {
    if (token && params.get('open') === 'inspiration') window.location.replace('/inspiration?t=' + encodeURIComponent(token))
  }, [token, params])
  return <JourneyView token={token} testEnabled={params.get('test') === '1'} />
}

/**
 * The Client Hub as one person sees it. `token` is their Hub token, or a team member's preview pass
 * (isPreviewToken), which reads exactly the same way and saves nothing. `banner` replaces the
 * built-in preview banner (the overlay brings its own, with the recipient picker);
 * `conceptDrawingHost: false` when the page around it already mounts one (OutlineView does).
 */
export function JourneyView({ token, testEnabled = false, banner, conceptDrawingHost = true }) {
  const j = useJourney(token)
  const preview = isPreviewToken(token)

  // /journey is a standalone, public route — but ClerkProvider wraps the WHOLE app (main.jsx),
  // so a team member who's signed in elsewhere (same browser) IS signed in here too, even though
  // this page never requires it. Forwarding that session (services/journey.js's
  // getOptionalStaffToken) is what lets the server recognize "this is our own team, not the
  // client" — see api/_shareDocs.js's resolveStaffView. Mirrors SketchPage.jsx's identical
  // window.__clerkGetToken pattern for the same reason (no OutlineView on this route to set it).
  const { getToken } = useAuth()
  useEffect(() => { window.__clerkGetToken = getToken }, [getToken])

  // Admin/QA test environment — gated by ?test=1 (testEnabled) so real clients never see it.
  const [tests, setTests] = useState({ billingHold: false })

  // Auto billing hold — initialize from localStorage so repeat visitors see the hold
  // instantly, before the async billing API call completes.
  const [autoBillingHold, setAutoBillingHold] = useState(() => {
    if (!token || preview) return false
    try { const c = JSON.parse(localStorage.getItem('journey-billing:' + token) || 'null'); return !!(c && c.held) } catch { return false }
  })
  const [billingPayUrl, setBillingPayUrl] = useState(() => {
    if (!token || preview) return null
    try { const c = JSON.parse(localStorage.getItem('journey-billing:' + token) || 'null'); return (c && c.payUrl) || null } catch { return null }
  })
  const billingHold = (testEnabled && tests.billingHold) || autoBillingHold

  // Billing summary + open ref — passed to JourneyShell so the Roadmap drawer can show
  // the Invoices shortcut and open the billing drawer without the two components merging.
  const [billingSummary, setBillingSummary] = useState({ hasUnpaid: false, hasInvoices: false })
  const billingOpenRef = useRef(null)

  // Inspiration board (docs/INSPIRATION_BOARD.md) — its own tab at /inspiration?t=<token> (Matt,
  // 2026-10-13: a new tab, not an overlay, and no Roadmap card). The top-right button opens it; an
  // older Hub link ending in &open=inspiration is forwarded there. The button shows from the first Hub
  // link, prospects included (project.inspirationOpen, always true since 2026-11-02).
  const openInspiration = () => window.open('/inspiration?t=' + encodeURIComponent(token || ''), '_blank')

  return (
    <div style={{ position: 'fixed', inset: 0, overflow: 'hidden', background: '#07272D' }}>
      <MistyBackground />
      {/* Opens on 'open-concept-drawing-modal' — the Sketch ribbon's PDF-backed items dispatch it
          (SketchRibbon.jsx), same event/component the /present surface already uses. */}
      {conceptDrawingHost && <ConceptDrawingHost />}

      {j.status === 'loading' && (
        <Centered>
          <div style={{ fontFamily: '"Montserrat", sans-serif', fontSize: 13, fontWeight: 500, letterSpacing: '0.34em', color: 'rgba(255,255,255,0.92)' }}>
            ALDER JAMES
          </div>
        </Centered>
      )}

      {j.status === 'notfound' && (
        <Centered>
          <div style={{ fontFamily: '"Montserrat", sans-serif', fontSize: 20, fontWeight: 300, letterSpacing: '0.04em', marginBottom: 10 }}>
            This link is no longer active
          </div>
          <div style={{ fontFamily: '"DM Sans", sans-serif', fontSize: 14, color: 'rgba(255,255,255,0.6)' }}>
            Please use the most recent link from your team, or contact us and we&rsquo;ll send a new one.
          </div>
        </Centered>
      )}

      {j.status === 'error' && <Centered>We couldn&rsquo;t load this just now. Please try again in a moment.</Centered>}

      {(j.status === 'pin_required' || j.status === 'pin_locked') && (
        <JourneyPinGate
          onSubmit={j.submitPin}
          busy={j.pinBusy}
          error={j.pinError === 'pin_invalid'}
          locked={j.status === 'pin_locked'}
          retryAt={j.pinRetryAt}
          onRequestReset={j.requestReset}
        />
      )}

      {preview && (banner !== undefined ? banner : (
        <div style={{
          position: 'fixed', top: 0, left: 0, right: 0, zIndex: 50, height: 30,
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          background: 'rgba(184,84,10,0.92)', color: '#fff',
          fontFamily: '"DM Sans", sans-serif', fontSize: 12, fontWeight: 500,
        }}>
          Preview{j.recipientName ? ' as ' + j.recipientName : ''}. Try anything: nothing here is saved or sent.
        </div>
      ))}

      {j.status === 'ready' && j.isStaffView && (
        <div style={{
          position: 'fixed', top: 0, left: 0, right: 0, zIndex: 50, height: 30,
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          background: 'rgba(184,84,10,0.92)', color: '#fff',
          fontFamily: '"DM Sans", sans-serif', fontSize: 12, fontWeight: 500,
        }}>
          You&rsquo;re viewing this as team — no client notifications or view records are triggered.
        </div>
      )}

      {j.status === 'ready' && (
        <div style={billingHold ? { filter: 'blur(3px)', pointerEvents: 'none', userSelect: 'none' } : undefined}>
          <JourneyShell
            project={j.project}
            entries={j.entries}
            phases={j.phases}
            index={j.index}
            total={j.total}
            responseFor={j.responseFor}
            respond={j.respond}
            goNext={j.goNext}
            goPrev={j.goPrev}
            goTo={j.goTo}
            token={token}
            subscribedEmail={j.subscribedEmail}
            billingSummary={billingSummary}
            billingOpenRef={billingOpenRef}
          />
        </div>
      )}

      {j.status === 'ready' && !billingHold && j.project?.inspirationOpen && (
        <button data-tour="inspiration" onClick={openInspiration} aria-label="Open your inspiration board in a new tab"
          style={{
            position: 'fixed', top: 34, right: 24, zIndex: 3, display: 'flex', alignItems: 'center', gap: 8,
            background: 'transparent', border: 'none', cursor: 'pointer', padding: 6, opacity: 0.7,
            fontFamily: '"Montserrat", sans-serif', fontSize: 9, fontWeight: 500, letterSpacing: '0.2em',
            textTransform: 'uppercase', color: 'rgba(255,255,255,0.9)', transition: 'opacity 0.2s',
          }}
          onMouseEnter={e => { e.currentTarget.style.opacity = 1 }}
          onMouseLeave={e => { e.currentTarget.style.opacity = 0.7 }}>
          <svg width="14" height="14" viewBox="0 0 14 14" fill="none" stroke="currentColor" strokeWidth="1.2" aria-hidden="true">
            <rect x="1" y="1" width="5" height="7" rx="1" /><rect x="8" y="1" width="5" height="4" rx="1" />
            <rect x="1" y="10" width="5" height="3" rx="1" /><rect x="8" y="7" width="5" height="6" rx="1" />
          </svg>
          <span>Inspiration</span>
        </button>
      )}

      {/* Getting started (docs/CLIENT_HUB_TOUR.md) — the client's own walkthrough of the Hub. Not for
          a signed-in team member on a real link, not behind a billing hold, and not on the
          anticipation screen (no entries yet, nothing to show). A preview pass gets it, saving nothing. */}
      {j.status === 'ready' && !billingHold && !j.isStaffView && (j.entries || []).length > 0 && (
        <HubTour token={token} tour={j.tour} setTour={j.setTour} project={j.project} billingSummary={billingSummary} />
      )}

      {/* Billing — always mounted once journey is ready so it can detect overdue invoices and
          fire onBillingHold. Renders no visible UI until at least one invoice has been issued. */}
      {/* Never for an owner's rep: no invoices, no balance, no billing hold (docs/CLIENT_HUB_OWNERS_REP.md). */}
      {j.status === 'ready' && j.project?.viewerRole !== 'owners_rep' && (
        <JourneyBilling
          token={token}
          onBillingHold={(held, payUrl) => {
            setAutoBillingHold(held)
            if (payUrl) setBillingPayUrl(payUrl)
          }}
          openRef={billingOpenRef}
          onSummary={s => setBillingSummary(s)}
        />
      )}

      {/* Blocking states render above the (blurred) journey */}
      {billingHold && <BillingHoldOverlay firmName="Alder James" payUrl={billingPayUrl} />}

      {/* Admin/QA test controls — always topmost so we can't lock ourselves out */}
      {testEnabled && <TestPanel tests={tests} onChange={setTests} />}
    </div>
  )
}

function Centered({ children }) {
  return (
    <div style={{
      position: 'fixed', inset: 0, zIndex: 1, display: 'flex', flexDirection: 'column',
      alignItems: 'center', justifyContent: 'center', textAlign: 'center', padding: 24,
      fontFamily: '"DM Sans", sans-serif', fontSize: 15, color: 'rgba(255,255,255,0.85)',
    }}>
      {children}
    </div>
  )
}
